import { useMemo, useState } from "react";
import useFetch from "../../hooks/useFetch";
import { usePagination } from "../../hooks/usePagination";
import useModal from "../../hooks/useModal";
import { message } from "antd";
import { approvePayment, rejectPayment, getPayments } from "../../services/payment";
import useAuth from "../../hooks/useAuth";
import { IPayment } from "../../types/interfaces";

function usePayments() {
  const { onTableChange, tableParams } = usePagination();
  const { rolePermissions, userData } = useAuth();
  
  const reviewModal = useModal();

  const memoizedPagination = useMemo(
    () => ({
      current: tableParams.pagination.current,
      pageSize: tableParams.pagination.pageSize,
      sorter: tableParams.sorter,
    }),
    [
      tableParams.pagination.current,
      tableParams.pagination.pageSize,
      tableParams.sorter,
    ],
  );

  const { data: rawData, error, loading, total, refetch } = useFetch({
    service: getPayments,
    pagination: memoizedPagination,
  });

  // Client side filtering for Empresa users (although backend should do this, adding for UI safety)
  const isEmpresa = userData?.type === "empresa" || userData?.type === "empleado";
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);

  const filteredData = useMemo(() => {
    if (!rawData) return [];
    
    let result = rawData as IPayment[];

    // 1. Role-based filter
    if (isEmpresa && userData?.id) {
      result = result.filter(p => p.edges?.user?.id === userData.id);
    }
    
    // 2. Status filter
    if (statusFilter && statusFilter !== "all") {
      result = result.filter(p => p.status === statusFilter);
    }

    // 3. Search term (Company Name)
    if (searchTerm && searchTerm.trim() !== "") {
      const lowerTerm = searchTerm.toLowerCase();
      result = result.filter(p => {
        const companyName = p.edges?.user?.name?.toLowerCase() || "";
        return companyName.includes(lowerTerm);
      });
    }

    // 4. Date range filter
    if (dateRange && dateRange[0] && dateRange[1]) {
      const start = new Date(dateRange[0]).getTime();
      const end = new Date(dateRange[1]).getTime() + 86400000; // Add 1 day to include the end date fully
      result = result.filter(p => {
        if (!p.dueDate) return false;
        const pDate = new Date(p.dueDate).getTime();
        return pDate >= start && pDate <= end;
      });
    }

    return result;
  }, [rawData, isEmpresa, userData, statusFilter, searchTerm, dateRange]);


  const [currentPayment, setCurrentPayment] = useState<IPayment | null>(null);

  const handleApprove = async (id: number) => {
    try {
      reviewModal.loading.start();
      await approvePayment(id);
      message.success("Pago aprobado con éxito. La suscripción ha sido actualizada.");
      handleCancel();
      refetch();
    } catch (error) {
      message.error("Error al aprobar el pago");
    } finally {
      reviewModal.loading.stop();
    }
  };

  const handleReject = async (id: number) => {
    try {
      reviewModal.loading.start();
      await rejectPayment(id);
      message.success("Pago marcado como fallido/rechazado.");
      handleCancel();
      refetch();
    } catch (error) {
      message.error("Error al rechazar");
    } finally {
      reviewModal.loading.stop();
    }
  };

  const handleCancel = () => {
    reviewModal.loading.stop();
    reviewModal.closeModal();
    setCurrentPayment(null);
  };

  return {
    data: filteredData,
    error,
    loading,
    total: isEmpresa ? filteredData.length : total,
    refetch,
    tableParams,
    onTableChange,
    reviewModal,
    handleApprove,
    handleReject,
    handleCancel,
    currentPayment,
    setCurrentPayment,
    rolePermissions,
    isEmpresa,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    dateRange,
    setDateRange
  };
}

export default usePayments;
